/**
 * The front door's one call (docs/specs/cloud/front-door.md §2): the signed-in person's companies, read from their own
 * memberships as the company page reads them (row level security returns only their rows).
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { isClientRole, normaliseTenantRole } from '../access/roles.ts';

export interface Company {
  readonly slug: string;
  readonly name: string;
  /** Staff of the company, or a user from one of its clients (staff wins when both). */
  readonly as: 'staff' | 'client';
}

export interface FrontDoorAccess {
  readonly platformAdmin: boolean;
  /** By name. */
  readonly companies: readonly Company[];
}

interface TenantEmbed {
  readonly slug: string;
  readonly name: string;
}

function one<T>(v: T | T[] | null | undefined): T | null {
  if (Array.isArray(v)) return v[0] ?? null;
  return v ?? null;
}

/** The companies this person may sign in to, and whether they are a Braynewave platform admin. */
export async function loadCompanies(sb: SupabaseClient, userId: string): Promise<FrontDoorAccess> {
  const [me, mem, cmem] = await Promise.all([
    sb.from('app_user').select('platform_admin').eq('id', userId).maybeSingle(),
    sb.from('membership').select('tenant_id, role, tenant:tenant_id (id, slug, name)').eq('user_id', userId),
    sb
      .from('client_membership')
      .select(
        'client_org_id, role, client_org:client_org_id (id, tenant_id, tenant:tenant_id (id, slug, name))',
      )
      .eq('user_id', userId),
  ]);
  for (const r of [me, mem, cmem]) if (r.error) throw new Error(r.error.message);
  const found = new Map<string, Company>();
  for (const raw of (mem.data ?? []) as unknown as {
    role: string;
    tenant: TenantEmbed | TenantEmbed[] | null;
  }[]) {
    const t = one(raw.tenant);
    if (t && normaliseTenantRole(raw.role)) found.set(t.slug, { slug: t.slug, name: t.name, as: 'staff' });
  }
  for (const raw of (cmem.data ?? []) as unknown as {
    role: string;
    client_org:
      | { tenant: TenantEmbed | TenantEmbed[] | null }
      | { tenant: TenantEmbed | TenantEmbed[] | null }[]
      | null;
  }[]) {
    const t = one(one(raw.client_org)?.tenant);
    if (t && isClientRole(raw.role) && !found.has(t.slug))
      found.set(t.slug, { slug: t.slug, name: t.name, as: 'client' });
  }
  return {
    platformAdmin: Boolean((me.data as { platform_admin?: boolean } | null)?.platform_admin),
    companies: [...found.values()].sort((a, b) => a.name.localeCompare(b.name, 'en-AU')),
  };
}
